import { CmsValue } from 'core/type/BootstrapConfig.types';
import { OfferNotificationType, SelectedOfferState } from 'component/page/detail/store/availabilityIndex.types';
import { createSelector, Dispatch } from '@reduxjs/toolkit';
import {
    selectAllOfferOptions,
    selectAvailabilityIsLoading,
    selectIsShowOptionSpecNotification,
    selectOfferGroupIdFromSelectedOfferGroup,
    selectOfferSelected,
    selectPossibleOfferOptions,
    selectSelectedSpecifications,
} from 'component/page/detail/store/availabilitySelectorIndex';
import { selectConfigFromBootstrap, selectExperimentsFromBootstrap } from 'store/slice/bootstrap';
import { Specification } from 'gql/autogen/Specification';
import { EnrichedOfferOption } from 'gql/autogen/EnrichedOfferOption';
import { setOfferNotification, setOfferOption } from 'component/page/detail/store/availabilityIndex';
import { isOn } from 'core/window/bootstrapConfig';
import { FeatureName } from 'core/constant/FeatureName';

interface OwnProps {
    setIsDisplayWarning: (isDisplayWarning: boolean) => void;
    isDisplayWarning: boolean;
}

interface StateProps {
    cms: CmsValue;
    selectedOffer: SelectedOfferState;
    selectedSpecifications: Specification[];
    possibleOfferOptions: EnrichedOfferOption[];
    allOfferOptions: EnrichedOfferOption[];
    selectedOfferId: number;
    isShowOptionSpecNotification: boolean;
    isAvailabilityLoading: boolean;
    isSortOfferOptionEnabled: boolean;
}

interface DispatchProps {
    setOfferOption: (offerOptionId: number, selectedSpecifications: Specification[], newSelectedSpecification: Specification) => void;
    setFullOfferOption: (offerOptionId: number, selectedSpecifications: Specification[]) => void;
    clearOfferOptions: (offerOptionId: number) => void;
}

export const mapStateToProps = createSelector(
    selectConfigFromBootstrap,
    selectOfferSelected,
    selectSelectedSpecifications,
    selectPossibleOfferOptions,
    selectAllOfferOptions,
    selectOfferGroupIdFromSelectedOfferGroup,
    selectIsShowOptionSpecNotification,
    selectAvailabilityIsLoading,
    selectExperimentsFromBootstrap,
    (
        config,
        selectedOffer,
        selectedSpecifications,
        possibleOfferOptions,
        allOfferOptions,
        selectedOfferId,
        isShowOptionSpecNotification,
        isAvailabilityLoading,
        experiments,
    ): StateProps => {
        return {
            cms: config.cms,
            selectedOffer,
            selectedSpecifications,
            possibleOfferOptions,
            allOfferOptions,
            selectedOfferId,
            isShowOptionSpecNotification,
            isAvailabilityLoading,
            isSortOfferOptionEnabled: isOn(FeatureName.SortOfferOptionAscending, config),
        };
    },
);

export const mapDispatchToProps = (dispatch: Dispatch): DispatchProps => ({
    setOfferOption: (offerOptionId: number, selectedSpecifications: Specification[], newSelectedSpecification: Specification) => {
        const selectedOptions = selectedSpecifications.filter((spec) => spec.key !== newSelectedSpecification.key);
        selectedOptions.push(newSelectedSpecification);
        dispatch(setOfferOption({ offerId: offerOptionId, selectedOptions }));
    },
    setFullOfferOption: (offerOptionId: number, selectedSpecifications: Specification[]) => {
        dispatch(setOfferOption({ offerId: offerOptionId, selectedOptions: selectedSpecifications }));
    },
    clearOfferOptions: (offerOptionId: number) => {
        dispatch(setOfferNotification({ [OfferNotificationType.OptionSpec]: false }));
        dispatch(setOfferOption({ offerId: offerOptionId, selectedOptions: [] }));
    },
});

export type Props = OwnProps & StateProps & DispatchProps;
